import { Loader2, RefreshCw, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { UpdateState } from "@/lib/pwa/serviceWorkerClient";

interface UpdateReadyBannerProps {
	state: UpdateState;
	onApply: () => void;
}

/**
 * The "new version ready" affordance. Purely presentational — it renders one
 * `UpdateState` and owns nothing but its own dismissal.
 *
 * Deliberately non-blocking: a card in the corner announced through a native
 * `<output>` polite live region (house idiom over `role="status"`) rather than
 * a `dialog` — no focus trap, no autofocus. A user standing in a paddock
 * mid-form must never have to dismiss a modal to keep working, and the update
 * is not urgent enough to interrupt anyone.
 *
 * | state                  | copy                              | action    | dismiss |
 * |------------------------|-----------------------------------|-----------|---------|
 * | current                | —                                 | —         | —       |
 * | deferred-work-pending  | title + `pwa.updatePendingWork`   | disabled  | yes     |
 * | deferred-user-busy     | title                             | enabled   | yes     |
 * | applying               | title                             | none      | no      |
 */
export function UpdateReadyBanner({ state, onApply }: UpdateReadyBannerProps) {
	const { t } = useTranslation();
	const [dismissed, setDismissed] = useState(false);
	const [dismissedFor, setDismissedFor] = useState<UpdateState>(state);

	// Dismissal is per-state, not per-session: any transition re-surfaces the
	// banner. Silencing it forever would mean a user who waved away
	// "we'll update once your work syncs" never learns the update became
	// applicable. Adjusting state during render is React's documented pattern
	// for deriving from a changed input — the discarded pass costs nothing.
	const transitioned = dismissedFor !== state;
	if (transitioned) {
		setDismissedFor(state);
		setDismissed(false);
	}

	if (state === "current") return null;
	if (dismissed && !transitioned) return null;

	const applying = state === "applying";
	const workPending = state === "deferred-work-pending";

	return (
		<output
			aria-live="polite"
			className="fixed bottom-20 inset-x-4 md:bottom-6 md:left-auto md:right-6 md:w-80 z-50 bg-base-100 border border-base-300 rounded-2xl shadow-xl p-4 flex items-start gap-3"
		>
			<div className="flex-1 min-w-0">
				<p className="font-semibold text-sm flex items-center gap-2">
					{applying && (
						<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
					)}
					{t("pwa.updateAvailable")}
				</p>

				{workPending && (
					<p className="text-xs text-base-content/60 mt-0.5">
						{t("pwa.updatePendingWork")}
					</p>
				)}

				{!applying && (
					<button
						type="button"
						onClick={onApply}
						disabled={workPending}
						className="btn btn-primary btn-sm rounded-full mt-2 gap-1"
					>
						<RefreshCw className="h-3.5 w-3.5" />
						{t("pwa.update")}
					</button>
				)}
			</div>

			{!applying && (
				<button
					type="button"
					onClick={() => setDismissed(true)}
					aria-label={t("common.dismiss")}
					className="shrink-0 btn btn-ghost btn-xs btn-circle"
				>
					<X className="h-4 w-4" />
				</button>
			)}
		</output>
	);
}
